import { Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthService } from '../../../core/auth/auth.service';
import { TranslationService } from '../../../core/i18n/translation.service';
import { PORTAL_MODE } from '../../../core/auth/portal-mode';

@Component({
  selector: 'app-login-page',
  imports: [ReactiveFormsModule],
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.css',
})
export class LoginPageComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly i18n = inject(TranslationService);
  protected readonly isPlatformPortal = inject(PORTAL_MODE) === 'platform';
  protected readonly isSubmitting = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly submitAttempted = signal(false);

  protected readonly form = this.formBuilder.nonNullable.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required]],
  });

  get validationMessage(): string {
    if (this.submitAttempted() && this.form.invalid) {
      return this.i18n.t('auth.login.errors.requiredFields');
    }

    return this.errorMessage();
  }

  submit(): void {
    this.submitAttempted.set(true);

    if (this.form.invalid || this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    void this.authService
      .login(this.form.getRawValue())
      .then(() => {
        this.isSubmitting.set(false);

        const redirectTo = this.route.snapshot.queryParamMap.get('redirectTo') || '/app';
        void this.router.navigateByUrl(redirectTo);
      })
      .catch((error: unknown) => {
        this.isSubmitting.set(false);
        const key = error instanceof HttpErrorResponse && error.error?.code === 'tenant_license_expired'
          ? 'auth.login.errors.licenseExpired'
          : 'auth.login.errors.invalidCredentials';
        this.errorMessage.set(this.i18n.t(key));
      });
  }
}
